import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { rechargeWallet } from '../services/api';
import './StoreModal.css';

// GC packages: price * 10 = bonus SC
const packages = [
    { amount: 10000, price: 1.00, bestValue: false },
    { amount: 25000, price: 2.50, bestValue: false },
    { amount: 50000, price: 5.00, bestValue: false },
    { amount: 100000, price: 10.00, bestValue: true },
    { amount: 250000, price: 25.00, bestValue: false },
    { amount: 500000, price: 50.00, bestValue: false },
    { amount: 1000000, price: 100.00, bestValue: false }
];

// Format GC amount: show as K if >= 1000
const formatGCAmount = (amount) => {
    if (amount >= 1000) {
        return (amount / 1000).toFixed(2) + 'K';
    }
    return amount.toLocaleString();
};

const StoreModal = ({ isOpen, onClose }) => {
    const { user, refreshWallet } = useAuth();
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [purchasedAmount, setPurchasedAmount] = useState(null);
    const [isPurchasing, setIsPurchasing] = useState(false);

    const handlePackageSelect = (pkg, index) => {
        setSelectedPackage({ ...pkg, index });
    };

    const handlePurchase = async () => {
        if (!selectedPackage || !user?.user_id) {
            return;
        }
        
        setIsPurchasing(true);
        
        try {
            // Call recharge API: coin for GC, rd_money for SC (price * 10)
            const coin = selectedPackage.amount;
            const rdMoney = Math.round(selectedPackage.price * 10);
            
            await rechargeWallet(user.user_id, coin, rdMoney);
            
            // Refresh wallet after successful recharge
            await refreshWallet();
            
            // Show success modal
            setPurchasedAmount(selectedPackage.amount);
            setShowSuccess(true);
        } catch (error) {
            console.error('Recharge failed:', error);
            setShowError(true);
        } finally {
            setIsPurchasing(false);
        }
    };

    const handleSuccessClose = () => {
        setShowSuccess(false);
        setPurchasedAmount(null);
        setSelectedPackage(null);
        onClose();
    };

    const handleErrorClose = () => {
        setShowError(false);
    };

    if (!isOpen) return null;

    return (
        <div className="store-modal-overlay" onClick={onClose}>
            <div className="store-content" onClick={(e) => e.stopPropagation()}>
                <div className="store-header">
                    <h2 className="store-title">COIN STORE</h2>
                    <button className="close-store-btn" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>
                
                <div className="store-body">
                    <div className="store-subtitle">Gold Coins Packages</div>
                    
                    <div className="packages-grid">
                        {packages.map((pkg, index) => {
                            const bonusSC = Math.round(pkg.price * 10);
                            return (
                                <div 
                                    key={index}
                                    className={`package-card ${pkg.bestValue ? 'best-value' : ''} ${
                                        selectedPackage?.index === index ? 'selected' : ''
                                    }`}
                                    onClick={() => handlePackageSelect(pkg, index)}
                                >
                                    <div className="package-amount">
                                        {formatGCAmount(pkg.amount)} GC
                                    </div>
                                    {bonusSC > 0 && (
                                        <div className="package-bonus">+{bonusSC.toLocaleString()} SC</div>
                                    )}
                                    <div className="package-price">${pkg.price.toFixed(2)}</div>
                                </div>
                            );
                        })}
                    </div>
                    
                    <button 
                        className="purchase-btn"
                        onClick={handlePurchase}
                        disabled={!selectedPackage || isPurchasing}
                    >
                        {isPurchasing ? 'Processing...' : 'Purchase'}
                    </button>
                </div>
            </div>

            {/* Success Modal */}
            {showSuccess && (
                <div className="success-modal-overlay" onClick={handleSuccessClose}>
                    <div className="success-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="success-icon">
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <h3 className="success-title">Purchase Successful!</h3>
                        <div className="success-details">
                            <div className="success-amount">
                                {formatGCAmount(purchasedAmount)} GC
                            </div>
                            <div className="success-bonus">
                                +{Math.round((purchasedAmount / 10000) * 10).toLocaleString()} SC Bonus
                            </div>
                        </div>
                        <p className="success-message">
                            Your coins have been added to your account.
                        </p>
                        <button className="success-close-btn" onClick={handleSuccessClose}>
                            Awesome!
                        </button>
                    </div>
                </div>
            )}

            {/* Error Modal */}
            {showError && (
                <div className="success-modal-overlay" onClick={handleErrorClose}>
                    <div className="success-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="success-icon" style={{ color: '#ef4444' }}>
                            <i className="fas fa-times-circle"></i>
                        </div>
                        <h3 className="success-title">Recharge Failed</h3>
                        <p className="success-message">
                            Recharge failed, please try again later
                        </p>
                        <button className="success-close-btn" onClick={handleErrorClose}>
                            OK
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StoreModal;